import { useCallback, useEffect, useState } from 'react';
import {
  confirmSubscription,
  deactivateSubscription,
  getSubscriptionStatus,
  prepareSubscription,
} from './api.js';
import {
  assertPasskeySupport,
  describePasskeyError,
  runPasskeyCeremony,
  waitForPaint,
} from './passkey.js';

const IDLE_STATE = Object.freeze({ phase: 'idle', ceremony: null });

export function useSubscriptionFlow() {
  const [status, setStatus] = useState(null);
  const [flow, setFlow] = useState(IDLE_STATE);
  const [dialog, setDialog] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const refreshStatus = useCallback(async () => {
    const nextStatus = await getSubscriptionStatus();
    setStatus(nextStatus);
    return nextStatus;
  }, []);

  useEffect(() => {
    let active = true;
    getSubscriptionStatus()
      .then((nextStatus) => {
        if (active) {
          setStatus(nextStatus);
        }
      })
      .catch((error) => {
        if (active) {
          setDialog({ type: 'error', ...describePasskeyError(error) });
        }
      })
      .finally(() => {
        if (active) {
          setLoadingStatus(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const subscribe = useCallback(async () => {
    if (status?.subscription?.active) {
      setDialog({ type: 'success', subscription: status.subscription });
      return;
    }

    try {
      setFlow({ phase: 'checking', ceremony: null });
      await assertPasskeySupport();

      setFlow({ phase: 'preparing', ceremony: null });
      const prepared = await prepareSubscription();

      setFlow({ phase: 'requesting', ceremony: prepared.ceremony });
      await waitForPaint();
      const response = await runPasskeyCeremony(prepared);

      setFlow({ phase: 'verifying', ceremony: prepared.ceremony });
      const confirmed = await confirmSubscription({
        intentId: prepared.intent.id,
        ceremony: prepared.ceremony,
        response,
      });

      setStatus((current) => ({
        ...current,
        hasPasskey: true,
        subscription: confirmed.subscription,
      }));
      setDialog({ type: 'success', subscription: confirmed.subscription });
    } catch (error) {
      setDialog({ type: 'error', ...describePasskeyError(error) });
    } finally {
      setFlow(IDLE_STATE);
    }
  }, [status?.subscription?.active]);

  const deactivate = useCallback(async () => {
    try {
      setFlow({ phase: 'deactivating', ceremony: null });
      const result = await deactivateSubscription();
      setStatus((current) => ({ ...current, subscription: result.subscription }));
      setDialog(null);
    } catch (error) {
      setDialog({ type: 'error', ...describePasskeyError(error) });
    } finally {
      setFlow(IDLE_STATE);
    }
  }, []);

  return {
    status,
    flow,
    dialog,
    loadingStatus,
    subscribe,
    deactivate,
    refreshStatus,
    closeDialog: () => setDialog(null),
  };
}
